import React, { useCallback, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Button,
  Typography,
  Skeleton,
  Chip,
  Stack,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  Tooltip,
  Badge,
  Fade,
  Drawer,
  Divider,
  useMediaQuery,
  Fab,
  Backdrop,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  TuneRounded as TuneIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Favorite as FavoriteIcon,
} from "@mui/icons-material";

const ResponsiveFilter = ({
  initialLoading,
  uniqueDiveTypes,
  uniqueCities,
  uniqueStates,
  filter,
  handleFilterChange,
  handleClearFilters,
  isFilterActive,
  allDivesites,
  displayDivesites,
  // Mobile-specific props (now used for both mobile and desktop)
  drawerOpen,
  setDrawerOpen,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State for both mobile and desktop
  const [searchText, setSearchText] = useState("");
  // Initialize localFilters based on the incoming filter prop
  const [localFilters, setLocalFilters] = useState(filter || {});

  // Provide default values for filter properties
  const currentDiveType = filter?.diveType || null;
  const currentCity = filter?.city || null;
  const currentState = filter?.state || null;
  const currentLiked = filter?.liked || false; // This is now directly from the filter prop

  // Count active filters
  const activeFilterCount = Object.values(filter || {}).filter(Boolean).length;

  // Handlers for direct filter changes (when drawer is open)
  const handleDiveTypeChange = useCallback(
    (event, value) => {
      handleFilterChange(event, value, "diveType");
      // setLocalFilters((prev) => ({ ...prev, diveType: value })); // No need to update localFilters directly here, useEffect will sync
    },
    [handleFilterChange]
  );

  const handleCityChange = useCallback(
    (event, value) => {
      handleFilterChange(event, value, "city");
      // setLocalFilters((prev) => ({ ...prev, city: value })); // No need to update localFilters directly here, useEffect will sync
    },
    [handleFilterChange]
  );

  const handleStateChange = useCallback(
    (event, value) => {
      handleFilterChange(event, value, "state");
      // setLocalFilters((prev) => ({ ...prev, state: value })); // No need to update localFilters directly here, useEffect will sync
    },
    [handleFilterChange]
  );

  const handleLikedChange = useCallback(
    (event) => {
      const value = event.target.checked;
      handleFilterChange(event, value, "liked");
      // setLocalFilters((prev) => ({ ...prev, liked: value })); // No need to update localFilters directly here, useEffect will sync
    },
    [handleFilterChange]
  );

  const handleSearchChange = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);

  const removeFilter = useCallback(
    (filterType) => {
      // For desktop, directly clear the filter via the prop handler
      handleFilterChange(null, null, filterType);
      // For mobile preview, update localFilters too
      setLocalFilters((prev) => ({ ...prev, [filterType]: null }));
    },
    [handleFilterChange]
  );

  // Local filter handlers for preview mode (when drawer is open)
  const handleLocalFilterChange = useCallback((event, value, filterType) => {
    setLocalFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  }, []);

  const handleLocalLikedChange = useCallback((event) => {
    const value = event.target.checked;
    setLocalFilters((prev) => ({
      ...prev,
      liked: value,
    }));
  }, []);

  const applyFilters = useCallback(() => {
    Object.entries(localFilters).forEach(([key, value]) => {
      // Ensure that if a value is null (cleared in local state), it's passed as an empty string to clear the filter
      handleFilterChange(null, value === null ? "" : value, key);
    });
    setDrawerOpen(false);
  }, [localFilters, handleFilterChange, setDrawerOpen]);

  const clearAllFilters = useCallback(() => {
    setLocalFilters({}); // Clear local preview state
    handleClearFilters(); // Clear the actual filters via the prop handler
    setDrawerOpen(false);
  }, [handleClearFilters, setDrawerOpen]);

  const resetFilters = useCallback(() => {
    setLocalFilters(filter || {}); // Reset local filters to currently applied filters
  }, [filter]);

  const handleClose = useCallback(() => {
    resetFilters(); // Reset local state to applied filters on close
    setDrawerOpen(false);
  }, [resetFilters, setDrawerOpen]);

  const handleOpen = useCallback(() => {
    setLocalFilters(filter || {}); // Initialize local filters with current filters on open
    setDrawerOpen(true);
  }, [filter, setDrawerOpen]);

  // Utility functions
  const getFilteredOptions = useCallback((options, searchValue) => {
    if (!searchValue) return options;
    return options.filter((option) =>
      option.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, []);

  // Check if local filters are different from applied filters
  const hasUnappliedChanges =
    JSON.stringify(localFilters) !== JSON.stringify(filter || {});

  // Sync local filters when filter prop changes (e.g., when filters are applied/cleared)
  React.useEffect(() => {
    setLocalFilters(filter || {});
  }, [filter]);

  // Floating Action Button - Only show on mobile
  const FilterFAB = () => {
    if (!isMobile) return null;

    return (
      <Fab
        color="primary"
        onClick={handleOpen}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          boxShadow: theme.shadows[8],
          "&:hover": {
            transform: "scale(1.05)",
          },
          transition: "transform 0.2s ease-in-out",
        }}
      >
        <Badge
          badgeContent={activeFilterCount}
          color="secondary"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.75rem",
              minWidth: "20px",
              height: "20px",
              fontWeight: 600,
            },
          }}
        >
          <TuneIcon />
        </Badge>
      </Fab>
    );
  };

  // Unified Drawer Component for both mobile and desktop
  const FilterDrawer = () => {
    const drawerHeight = isMobile ? "85vh" : "70vh";

    const drawerContent = (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Filter Dive Sites
              </Typography>
              {activeFilterCount > 0 && (
                <Badge
                  badgeContent={activeFilterCount}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.75rem",
                      minWidth: "18px",
                      height: "18px",
                    },
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Results count */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing <strong>{displayDivesites?.length || 0}</strong> of{" "}
            <strong>{allDivesites?.length || 0}</strong> dive sites
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {initialLoading ? (
            <Stack spacing={3}>
              {[...Array(4)].map((_, index) => (
                <Box key={index}>
                  <Skeleton
                    variant="text"
                    width={100}
                    height={24}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton variant="rounded" height={56} />
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack spacing={3}>
              {/* Quick Actions */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  >
                    Quick Actions
                  </Typography>
                  {isFilterActive && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={clearAllFilters}
                      startIcon={<ClearIcon />}
                      color="error"
                      sx={{ borderRadius: 2 }}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>

                {/* Active Filters Preview */}
                {Object.values(isMobile ? localFilters : filter || {}).some(
                  Boolean
                ) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: "block", fontWeight: 600 }}
                    >
                      {isMobile ? "Preview Filters" : "Active Filters"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {Object.entries(
                        isMobile ? localFilters : filter || {}
                      ).map(([key, value]) => {
                        if (!value) return null;
                        let displayKey, displayValue;

                        if (key === "liked") {
                          displayKey = "Liked";
                          displayValue = "Yes";
                        } else {
                          displayKey =
                            key === "diveType"
                              ? "Type"
                              : key.charAt(0).toUpperCase() + key.slice(1);
                          displayValue = value;
                        }

                        return (
                          <Chip
                            key={key}
                            label={`${displayKey}: ${displayValue}`}
                            size="small"
                            onDelete={
                              () =>
                                isMobile
                                  ? handleLocalFilterChange(null, null, key) // For local preview
                                  : removeFilter(key) // For direct desktop interaction
                            }
                            deleteIcon={<ClearIcon />}
                            sx={{
                              bgcolor:
                                key === "liked"
                                  ? alpha(theme.palette.secondary.main, 0.1)
                                  : alpha(theme.palette.primary.main, 0.1),
                              color:
                                key === "liked"
                                  ? "secondary.main"
                                  : "primary.main",
                              "& .MuiChip-deleteIcon": {
                                color:
                                  key === "liked"
                                    ? "secondary.main"
                                    : "primary.main",
                              },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* Liked Filter */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.secondary.main, 0.05),
                  borderRadius: 2,
                  border: `1px solid ${alpha(
                    theme.palette.secondary.main,
                    0.1
                  )}`,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        isMobile ? localFilters.liked || false : currentLiked
                      }
                      onChange={
                        isMobile ? handleLocalLikedChange : handleLikedChange
                      }
                      color="secondary"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FavoriteIcon
                        sx={{
                          color: (
                            isMobile
                              ? localFilters.liked || false
                              : currentLiked
                          )
                            ? "secondary.main"
                            : "text.secondary",
                          fontSize: "1.2rem",
                        }}
                      />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Show Only Liked Dive Sites
                      </Typography>
                    </Box>
                  }
                  sx={{ margin: 0 }}
                />
              </Box>

              {/* Dive Type */}
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  Dive Type
                </Typography>
                <Autocomplete
                  options={getFilteredOptions(uniqueDiveTypes, searchText)}
                  value={
                    isMobile ? localFilters.diveType || null : currentDiveType
                  }
                  onChange={
                    isMobile
                      ? (event, value) =>
                          handleLocalFilterChange(event, value, "diveType")
                      : handleDiveTypeChange
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select dive type"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* City */}
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  City
                </Typography>
                <Autocomplete
                  options={getFilteredOptions(uniqueCities, searchText)}
                  value={isMobile ? localFilters.city || null : currentCity}
                  onChange={
                    isMobile
                      ? (event, value) =>
                          handleLocalFilterChange(event, value, "city")
                      : handleCityChange
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select city"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* State */}
              <Box>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  State
                </Typography>
                <Autocomplete
                  options={getFilteredOptions(uniqueStates, searchText)}
                  value={isMobile ? localFilters.state || null : currentState}
                  onChange={
                    isMobile
                      ? (event, value) =>
                          handleLocalFilterChange(event, value, "state")
                      : handleStateChange
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select state"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Stats */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Available Options
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {uniqueDiveTypes?.length || 0} dive types •{" "}
                  {uniqueCities?.length || 0} cities •{" "}
                  {uniqueStates?.length || 0} states
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>

        {/* Footer - Only show apply/reset buttons on mobile */}
        {isMobile && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              position: "sticky",
              bottom: 0,
            }}
          >
            <Stack spacing={1}>
              <Button
                variant="contained"
                onClick={applyFilters}
                startIcon={<CheckIcon />}
                fullWidth
                size="large"
                disabled={
                  !hasUnappliedChanges &&
                  !Object.values(localFilters).some(Boolean) // Disable if no changes AND no filters are currently active (to prevent applying empty filters)
                }
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Apply Filters
              </Button>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={clearAllFilters}
                  startIcon={<ClearIcon />}
                  fullWidth
                  disabled={!isFilterActive}
                  sx={{ borderRadius: 2 }}
                >
                  Clear All
                </Button>
                <Button
                  variant="text"
                  onClick={resetFilters}
                  fullWidth
                  disabled={!hasUnappliedChanges}
                  sx={{ borderRadius: 2 }}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    );

    return (
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            height: drawerHeight,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bgcolor: "background.default",
            maxWidth: isMobile ? "100%" : "600px",
            mx: isMobile ? 0 : "auto",
            left: isMobile ? 0 : "50%",
            transform: isMobile ? "none" : "translateX(-50%)",
          },
        }}
        SlideProps={{
          direction: "up",
        }}
        // Add backdrop for desktop for better focus
        BackdropComponent={!isMobile ? Backdrop : undefined}
        BackdropProps={
          !isMobile
            ? {
                sx: {
                  bgcolor: alpha(theme.palette.common.black, 0.3),
                },
              }
            : undefined
        }
      >
        {drawerContent}
      </Drawer>
    );
  };

  return (
    <>
      <FilterFAB />
      <FilterDrawer />
    </>
  );
};

export default React.memo(ResponsiveFilter);
